<script setup lang="ts">
import {onBeforeUnmount, onMounted, useTemplateRef, watch, ref} from "vue";
import { defaultValueCtx, Editor, rootCtx, commandsCtx } from "@milkdown/kit/core";
import {
  commonmark,
  toggleStrongCommand,
  toggleEmphasisCommand,
  wrapInBulletListCommand,
  wrapInOrderedListCommand,
  wrapInBlockquoteCommand,
  insertImageCommand,
  wrapInHeadingCommand
} from "@milkdown/kit/preset/commonmark";
import { gfm, toggleStrikethroughCommand } from "@milkdown/kit/preset/gfm";
import { nord } from "@milkdown/theme-nord";
import { history, undoCommand, redoCommand } from "@milkdown/kit/plugin/history";
import { getMarkdown, replaceAll } from "@milkdown/kit/utils";
import { listener, listenerCtx } from "@milkdown/kit/plugin/listener";

const props = defineProps({
  inputText: String
});

// Define the emit function
const emit = defineEmits<{
  contentChange: [content: string]
  textFocused: []
  textBlurred: [content: string]
  undoRedoStateChange: [canUndo: boolean, canRedo: boolean]
}>();

const textDiv = useTemplateRef("editable-text");
const isEditorFocused = ref(false);

let editor: Editor;
let isUpdatingFromProp = false; // Flag to prevent loops
let lastEmittedContent = ''; // Track last emitted content
let focusStartContent = ''; // Content when focus started
let debounceTimer: any = null;

const inputTextCopy = props.inputText ?? '';

watch(() => props.inputText, (newValue) => {
  if (editor && newValue !== undefined && newValue !== lastEmittedContent) {
    isUpdatingFromProp = true;
    editor.action((ctx) => {
      editor.action(replaceAll(newValue));
    });
    setTimeout(() => {
      isUpdatingFromProp = false;
    }, 50);
  }
  else if (!editor) {
    initEditor();
  }
});

onMounted(async () => {
  initEditor();
});

onBeforeUnmount(() => {
  if (editor) {
    editor.destroy();
  }
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
});

async function initEditor() {
  editor = await Editor.make()
    .config((ctx) => {
      ctx.set(rootCtx, textDiv.value);
      ctx.set(defaultValueCtx, inputTextCopy);

      ctx.get(listenerCtx).markdownUpdated((ctx, markdown, prevMarkdown) => {
        if (markdown !== prevMarkdown && !isUpdatingFromProp) {
          lastEmittedContent = markdown;

          // Emit immediate change for dirty state tracking
          emit('contentChange', markdown);

          // Debounce the undo/redo state checking to avoid performance issues
          if (debounceTimer) {
            clearTimeout(debounceTimer);
          }
          debounceTimer = setTimeout(() => {
            checkUndoRedoState();
          }, 100);
        }
      });

      ctx.get(listenerCtx).focus(() => {
        isEditorFocused.value = true;
        focusStartContent = lastEmittedContent;
        emit('textFocused');
      });

      ctx.get(listenerCtx).blur(() => {
        isEditorFocused.value = false;
        const currentContent = getCurrentContent();

        // Only emit blur if content actually changed during this focus session
        if (currentContent !== focusStartContent) {
          emit('textBlurred', currentContent);
        }
      });
    })
    .config(nord)
    .use(commonmark)
    .use(gfm)
    .use(history)
    .use(listener)
    .create();

  console.log(editor);

  // Initial state check
  setTimeout(() => {
    checkUndoRedoState();
  }, 100);
}

// Get current content from editor
function getCurrentContent(): string {
  if (!editor) return '';
  try {
    return editor.action(getMarkdown()) || '';
  } catch {
    return '';
  }
}

// Check if undo/redo is available and emit state
function checkUndoRedoState() {
  if (!editor) return;

  // Milkdown doesn't expose undo/redo state directly, so we use a workaround
  // We'll try to check if undo/redo commands would succeed
  try {
    // This is a simplified approach - in reality, you might need to track this yourself
    // or use Milkdown's internal state if available
    const canUndo = true; // Assume true for now - you'd need to implement proper checking
    const canRedo = true; // Assume true for now - you'd need to implement proper checking

    emit('undoRedoStateChange', canUndo, canRedo);
  } catch (error) {
    console.warn('Could not check undo/redo state:', error);
  }
}

// Expose undo/redo functions
function undoEditor() {
  if (editor) {
    try {
      editor.action((ctx) => {
        ctx.get(commandsCtx).call(undoCommand.key);
      });
      setTimeout(() => checkUndoRedoState(), 10);
    } catch (error) {
      console.warn('Undo failed:', error);
    }
  }
}

function redoEditor() {
  if (editor) {
    try {
      editor.action((ctx) => {
        ctx.get(commandsCtx).call(redoCommand.key);
      });
      setTimeout(() => checkUndoRedoState(), 10);
    } catch (error) {
      console.warn('Redo failed:', error);
    }
  }
}

// Create a snapshot for external undo system
function createSnapshot() {
  return getCurrentContent();
}

// Restore from a snapshot
function restoreSnapshot(content: string) {
  if (editor && content !== getCurrentContent()) {
    isUpdatingFromProp = true;
    editor.action(replaceAll(content));
    setTimeout(() => {
      isUpdatingFromProp = false;
      checkUndoRedoState();
    }, 50);
  }
}

function focusEditor() {
  if (!textDiv.value) return false;

  const editableEl = textDiv.value.querySelector('[contenteditable="true"]') as HTMLElement | null;
  if (!editableEl) return false;

  editableEl.focus();

  const selection = window.getSelection();
  if (!selection) return true;

  const range = document.createRange();
  range.selectNodeContents(editableEl);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);

  return true;
}

defineExpose({
  undoEditor,
  redoEditor,
  createSnapshot,
  restoreSnapshot,
  getCurrentContent,
  focusEditor
});

// ... rest of your existing functions remain the same
function executeCommand(commandKey: any, payload?: any) {
  editor.action((ctx) => {
    const commandManager = ctx.get(commandsCtx);
    commandManager.call(commandKey, payload);
  });
}

function retreiveMarkdown() {
  const markdown = editor.action(getMarkdown());
  console.log(markdown);
}

function toggleBold() {
  executeCommand(toggleStrongCommand.key);
}

function toggleItalic() {
  executeCommand(toggleEmphasisCommand.key);
}

function toggleStrikethrough() {
  executeCommand(toggleStrikethroughCommand.key);
}

function toggleUnderline() {
  console.warn('Underline is not supported in standard Markdown/CommonMark');
}

function bulletedList() {
  executeCommand(wrapInBulletListCommand.key);
}

function orderedList() {
  executeCommand(wrapInOrderedListCommand.key);
}

function blockquote() {
  executeCommand(wrapInBlockquoteCommand.key);
}

function insertImage() {
  const src = prompt('Enter image URL:');
  if (src) {
    const alt = prompt('Enter alt text (optional):') || '';
    const title = prompt('Enter title (optional):') || '';

    executeCommand(insertImageCommand.key, {
      src,
      alt,
      title
    });
  }
}

function insertImageWithUpload() {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';

  input.onchange = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const src = e.target?.result as string;
        const alt = file.name;

        executeCommand(insertImageCommand.key, {
          src,
          alt,
          title: file.name
        });
      };
      reader.readAsDataURL(file);
    }
  };

  input.click();
}

function toggleH1() {
  executeCommand(wrapInHeadingCommand.key, 1);
}

function toggleH2() {
  executeCommand(wrapInHeadingCommand.key, 2);
}

function toggleH3() {
  executeCommand(wrapInHeadingCommand.key, 3);
}
</script>

<template>
  <!-- Template remains the same -->
  <div class="flex-1 overflow-auto">
    <div class="p-2 pt-0 w-full">
      <div ref="editable-text" class="w-full p-0"></div>
    </div>
  </div>

  <div v-if="isEditorFocused" id="text-toolbar" class="sticky bottom-0 bg-base-200 p-2 z-10 flex gap-2 border-t border-base-300">
    <button id="exec-bold" class="btn btn-ghost btn-sm p-0" @mousedown.prevent @click="toggleBold">
      <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-bold"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 5h6a3.5 3.5 0 0 1 0 7h-6z" /><path d="M13 12h1a3.5 3.5 0 0 1 0 7h-7v-7" /></svg>
    </button>
    <button id="exec-italic" class="btn btn-ghost btn-sm p-0" @mousedown.prevent @click="toggleItalic">
      <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-italic"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M11 5l6 0" /><path d="M7 19l6 0" /><path d="M14 5l-4 14" /></svg>
    </button>
    <button id="exec-strikethrough" class="btn btn-ghost btn-sm p-0" @mousedown.prevent @click="toggleStrikethrough">
      <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-strikethrough"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 12l14 0" /><path d="M16 6.5a4 2 0 0 0 -4 -1.5h-1a3.5 3.5 0 0 0 0 7h2a3.5 3.5 0 0 1 0 7h-1.5a4 2 0 0 1 -4 -1.5" /></svg>
    </button>
    <button id="exec-list" class="btn btn-ghost btn-sm p-0" @mousedown.prevent @click="bulletedList">
      <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-list"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 6l11 0" /><path d="M9 12l11 0" /><path d="M9 18l11 0" /><path d="M5 6l0 .01" /><path d="M5 12l0 .01" /><path d="M5 18l0 .01" /></svg>
    </button>
    <button id="exec-ordered-list" class="btn btn-ghost btn-sm p-0" @mousedown.prevent @click="orderedList">
      <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-list-numbers"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M11 6h9" /><path d="M11 12h9" /><path d="M12 18h8" /><path d="M4 16a2 2 0 1 1 4 0c0 .591 -.5 1 -1 1.5l-3 2.5h4" /><path d="M6 10v-6l-2 2" /></svg>
    </button>
    <button id="exec-blockquote" class="btn btn-ghost btn-sm p-0" @mousedown.prevent @click="blockquote">
      <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-blockquote"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6 15h15" /><path d="M21 19h-15" /><path d="M15 11h6" /><path d="M21 7h-6" /><path d="M9 9h1a1 1 0 1 1 -1 1v-2.5a2 2 0 0 1 2 -2" /><path d="M3 9h1a1 1 0 1 1 -1 1v-2.5a2 2 0 0 1 2 -2" /></svg>
    </button>
    <div class="grow flex justify-end gap-2">
      <button id="exec-h1" class="btn btn-ghost btn-sm p-0" @mousedown.prevent @click="toggleH1">
        <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-h-1"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M19 18v-8l-2 2" /><path d="M4 6v12" /><path d="M12 6v12" /><path d="M11 18h2" /><path d="M3 18h2" /><path d="M4 12h8" /><path d="M3 6h2" /><path d="M11 6h2" /></svg>
      </button>
      <button id="exec-h2" class="btn btn-ghost btn-sm p-0" @mousedown.prevent @click="toggleH2">
        <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-h-2"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M17 12a2 2 0 1 1 4 0c0 .591 -.417 1.318 -.816 1.858l-3.184 4.143l4 0" /><path d="M4 6v12" /><path d="M12 6v12" /><path d="M11 18h2" /><path d="M3 18h2" /><path d="M4 12h8" /><path d="M3 6h2" /><path d="M11 6h2" /></svg>
      </button>
      <button id="exec-h3" class="btn btn-ghost btn-sm p-0" @mousedown.prevent @click="toggleH3">
        <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-h-3"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M19 14a2 2 0 1 0 -2 -2" /><path d="M17 16a2 2 0 1 0 2 -2" /><path d="M4 6v12" /><path d="M12 6v12" /><path d="M11 18h2" /><path d="M3 18h2" /><path d="M4 12h8" /><path d="M3 6h2" /><path d="M11 6h2" /></svg>
      </button>
    </div>
  </div>
</template>
